import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, FileText, Search } from 'lucide-react';
import { callTranscriptService } from 'api/services/callTranscript.service';
import { isFeatureEnabled } from 'components/Layout/navVisibility';
import {
  CallTranscript,
  SPEAKER_LABEL,
  countMatches,
  formatOffset,
  highlight,
  isEmptyTranscript,
  transcriptAsText,
} from 'utils/callTranscript';

export const CALL_TRANSCRIPTS_FLAG = 'call-transcripts';

interface Props {
  callId?: string | null;
  organizationId?: string;
  /** Seek the recording player on this page to a second; absent when there is no player to seek. */
  onSeek?: (seconds: number) => void;
  /** Tests: the flag set (defaults to the build's REACT_APP_ENABLE_FEATURES). */
  enabledFeatures?: Set<string>;
}

type State =
  | { kind: 'loading' }
  | { kind: 'hidden' } // feature off server-side, or nothing visible to this org (404)
  | { kind: 'error' }
  | { kind: 'ok'; transcript: CallTranscript };

/** Copy with the Clipboard API, falling back to a selected textarea + execCommand. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the select-text fallback */
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = typeof document.execCommand === 'function' && document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => (
  <>
    {highlight(text, query).map((s, i) =>
      s.match ? (
        <mark key={i} className="bg-yellow-200 rounded px-0.5" data-testid="hit">
          {s.text}
        </mark>
      ) : (
        <React.Fragment key={i}>{s.text}</React.Fragment>
      ),
    )}
  </>
);

/**
 * The call's transcript from cpass's store: summaries on top, then the conversation as bubbles.
 * Tenancy is the server's: a 404 (feature off, or not this organisation's call) hides the panel.
 * Never logs transcript text.
 */
const CallTranscriptPanel: React.FC<Props> = ({ callId, organizationId, onSeek, enabledFeatures }) => {
  const enabled = isFeatureEnabled(CALL_TRANSCRIPTS_FLAG, enabledFeatures);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!enabled || !callId) return;
    setState({ kind: 'loading' });
    callTranscriptService
      .get(callId, organizationId)
      .then((r) => {
        if (!alive.current) return;
        setState(r.kind === 'none' ? { kind: 'hidden' } : { kind: 'ok', transcript: r.transcript });
      })
      .catch(() => {
        if (alive.current) setState({ kind: 'error' });
      });
    return () => {
      alive.current = false;
    };
  }, [enabled, callId, organizationId]);

  const matches = useMemo(
    () => (state.kind === 'ok' ? countMatches(state.transcript, query) : 0),
    [state, query],
  );

  if (!enabled || !callId || state.kind === 'hidden') return null;

  const header = (
    <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
      <FileText className="w-5 h-5" />
      Transcript
    </h4>
  );

  if (state.kind === 'loading') {
    return (
      <div data-testid="call-transcript-panel">
        {header}
        <p className="text-sm text-gray-500">Loading transcript…</p>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div data-testid="call-transcript-panel">
        {header}
        <p className="text-sm text-red-700">The transcript could not be loaded.</p>
      </div>
    );
  }

  const t = state.transcript;
  if (isEmptyTranscript(t)) {
    return (
      <div data-testid="call-transcript-panel">
        {header}
        <p className="text-sm text-gray-500" data-testid="transcript-empty">
          No transcript for this call
        </p>
      </div>
    );
  }

  const onCopy = async () => {
    const ok = await copyText(transcriptAsText(t));
    setCopied(ok ? 'ok' : 'failed');
    setTimeout(() => alive.current && setCopied('idle'), 2000);
  };

  return (
    <div data-testid="call-transcript-panel">
      {header}
      {(t.callSummary || t.transferCallSummary) && (
        <div className="bg-blue-50 rounded-lg p-4 mb-3 space-y-2">
          {t.callSummary && (
            <div>
              <p className="text-sm font-medium text-gray-700">Summary</p>
              <p className="text-sm whitespace-pre-wrap" data-testid="call-summary">
                <Highlighted text={t.callSummary} query={query} />
              </p>
            </div>
          )}
          {t.transferCallSummary && (
            <div>
              <p className="text-sm font-medium text-gray-700">Transfer summary</p>
              <p className="text-sm whitespace-pre-wrap" data-testid="transfer-summary">
                <Highlighted text={t.transferCallSummary} query={query} />
              </p>
            </div>
          )}
        </div>
      )}

      {t.lines.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
              <input
                type="search"
                aria-label="Search transcript"
                placeholder="Search transcript"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-8 pr-2 py-1.5 text-sm border rounded-lg"
              />
            </div>
            {query.trim() && (
              <span className="text-xs text-gray-500" data-testid="match-count">
                {matches} {matches === 1 ? 'match' : 'matches'}
              </span>
            )}
            <button
              type="button"
              onClick={onCopy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              <Copy className="w-4 h-4" />
              {copied === 'ok' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy'}
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 space-y-2 max-h-96 overflow-y-auto" data-testid="transcript-lines">
            {t.lines.map((l, i) => {
              const agentSide = l.speaker === 'agent' || l.speaker === 'tool' || l.speaker === 'system';
              return (
                <div key={i} className={`flex ${agentSide ? 'justify-start' : 'justify-end'}`} data-speaker={l.speaker}>
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      agentSide ? 'bg-white border' : 'bg-green-100'
                    } ${l.speaker === 'tool' ? 'italic text-gray-600' : ''}`}
                  >
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-0.5">
                      <span className="font-medium">{SPEAKER_LABEL[l.speaker]}</span>
                      {l.offsetSeconds !== null &&
                        (onSeek ? (
                          <button
                            type="button"
                            className="underline hover:text-blue-700"
                            title="Play from here"
                            onClick={() => onSeek(l.offsetSeconds as number)}
                          >
                            {formatOffset(l.offsetSeconds)}
                          </button>
                        ) : (
                          <span>{formatOffset(l.offsetSeconds)}</span>
                        ))}
                    </div>
                    <p className="whitespace-pre-wrap">
                      <Highlighted text={l.text} query={query} />
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {t.truncated && (
            <p className="text-xs text-gray-500 mt-1">This transcript was long and is shown cut short.</p>
          )}
        </>
      )}
    </div>
  );
};

export default CallTranscriptPanel;
